const map = new Map();

/**
 *
 * @param {string} url
 * @param {boolean} bypassCors
 * @return {Promise<Image>}
 */
export async function loadImage(url, bypassCors = false) {
  let _url;

  if (bypassCors) {
    _url = await fetchAsDataUrl(url);
  } else {
    _url = url;
  }

  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      resolve(img);
    };

    img.onerror = () => {
      reject(new Error(`Failed to load image: ${_url}`));
    };

    img.crossOrigin = 'anonymous';
    img.src = _url;
  });
}

/**
 *
 * @param {string} url
 * @param {boolean} bypassCors
 * @return {Promise<Image>}
 */
export function loadImageAndCache(url, bypassCors = false) {
  if (!map.has(url)) {
    const promise = loadImage(url, bypassCors);

    // Remove failed entries from cache so they can be retried
    promise.catch(() => map.delete(url));

    map.set(url, promise);
  }

  return map.get(url);
}

/**
 *
 * @param {string} url
 * @return {Promise<Blob>}
 */
export function fetchAsBlob(url) {
  return fetch(url).then(response => response.blob());
}

/**
 *
 * @param {Blob} blob
 * @return {Promise<String>}
 */
export function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onerror = reject;
    reader.onload = () => {
      resolve(reader.result);
    };

    reader.readAsDataURL(blob);
  });
}

/**
 *
 * @param {string} url
 * @return {Promise<String>}
 */
export async function fetchAsDataUrl(url) {
  return await blobToDataUrl(await fetchAsBlob(url));
}

export class UnknownImageTypeError extends Error {}

export class ImageValidationError extends Error {}

export class ImageSizeValidationError extends ImageValidationError {}

export class ImageFormatValidationError extends ImageValidationError {}

export const ImageMimeType = Object.freeze({
  JPEG: 'image/jpeg',
  PNG: 'image/png',
  GIF: 'image/gif',
  SVG: 'image/svg+xml',
});

export const FileSignatures = Object.freeze({
  [ImageMimeType.PNG]: ['89504e47'],
  [ImageMimeType.GIF]: ['47494638'],
  [ImageMimeType.JPEG]: [
    'ffd8ffe0',
    'ffd8ffe1',
    'ffd8ffe2',
    'ffd8ffe3',
    'ffd8ffe8',
  ],
});

/**
 *
 * @param {File} file
 * @return {Promise<string>}
 */
export async function getImageType(file) {
  // See https://stackoverflow.com/a/29672957/1224564
  const {
    target: { result },
  } = await new Promise(resolve => {
    const fileReader = new FileReader();

    fileReader.onloadend = e => {
      resolve(e);
    };

    fileReader.readAsArrayBuffer(file);
  });

  const arr = new Uint8Array(result).subarray(0, 4);
  const header = arr.reduce((acc, i) => {
    acc += i.toString(16);
    return acc;
  }, '');

  const mimeTypes = Object.keys(FileSignatures);

  for (let i = 0, len = mimeTypes.length; i < len; i++) {
    if (FileSignatures[mimeTypes[i]].includes(header)) {
      return mimeTypes[i];
    }
  }

  throw new UnknownImageTypeError('Unknown image type');
}

/**
 *
 * @param {File} file
 * @param {Number|null} maxFileSize
 * @param {String|null} mimeTypes
 * @return {Promise<boolean>}
 */
export async function validateImage(
  file,
  { maxFileSize = null, mimeTypes = null } = {}
) {
  if (!(file instanceof File)) {
    throw new Error('Unable to validate image. The provided file is invalid');
  }

  if (maxFileSize !== null && file.size > maxFileSize) {
    throw new ImageSizeValidationError(
      `File size exceeds the max allowed file size (${maxFileSize})`
    );
  }

  const allowedMimeTypes = mimeTypes || Object.values(ImageMimeType);

  if (allowedMimeTypes.includes(file.type)) {
    switch (file.type) {
      case ImageMimeType.PNG:
      case ImageMimeType.JPEG:
      case ImageMimeType.GIF: {
        let fileType;

        try {
          fileType = await getImageType(file);
        } catch (e) {
          if (e instanceof UnknownImageTypeError) {
            throw new ImageFormatValidationError('Invalid file type');
          }

          throw e;
        }

        if (!allowedMimeTypes.includes(fileType)) {
          throw new ImageFormatValidationError('Invalid file type');
        }

        break;
      }

      case ImageMimeType.SVG: {
        let imageUrl = URL.createObjectURL(file);

        try {
          // Find a better way to validate SVG than trying to create an Image.
          await loadImage(imageUrl);

          URL.revokeObjectURL(imageUrl);
        } catch (e) {
          URL.revokeObjectURL(imageUrl);

          throw new ImageFormatValidationError('Invalid file type');
        }

        break;
      }
    }
  } else {
    throw new ImageFormatValidationError('Invalid file type');
  }

  return true;
}

/**
 *
 * @param {File} file
 * @param {Number|null} maxFileSize
 * @param {String|null} mimeTypes
 * @return {Promise<boolean>}
 */
export async function isValidImage(
  file,
  { maxFileSize = null, mimeTypes = null } = {}
) {
  try {
    return await validateImage(file, { maxFileSize, mimeTypes });
  } catch (e) {
    return false;
  }
}

/**
 *
 * @param {string} dataURI
 * @return {Blob}
 */
export function dataUrlToBlob(dataURI) {
  const [header, data] = dataURI.split(',');
  const [mimeType] = header.split(':')[1].split(';');

  const byteStr = atob(data);
  const arrayBuffer = new ArrayBuffer(byteStr.length);
  const intArray = new Uint8Array(arrayBuffer);

  for (let i = 0; i < byteStr.length; i++) {
    intArray[i] = byteStr.charCodeAt(i);
  }

  return new Blob([arrayBuffer], { type: mimeType });
}

export class InvalidImageSizeError extends Error {}

/**
 *
 * @param {Image} image
 * @param {Boolean} throwError
 */
export function checkImageSizeNotZero(image, throwError = false) {
  if (!(image instanceof Image)) {
    throw new TypeError('The provided image is invalid.');
  }

  if (image.width === 0 || image.height === 0) {
    const message = `Unable to determine texture image's width/height (${image.width}×${image.height}). Your image may not be rendered on the canvas.\nIf you're using a SVG, please ensure it contains 'width' and 'height' attributes.\nSee https://bugzilla.mozilla.org/show_bug.cgi?id=700533 for more details.`;

    if (throwError) {
      throw new InvalidImageSizeError(message);
    }

    console.warn(message);
  }
}

/**
 *
 * @param {string} blobUrl
 * @return {Promise<Blob>}
 */
export function objectUrlToBlob(blobUrl) {
  return fetch(blobUrl).then(r => r.blob());
}

export function fitArea(
  { width, height },
  { width: targetWidth, height: targetHeight },
  expand = false
) {
  const o = {};

  if (!expand && width <= targetWidth && height <= targetHeight) {
    o.width = width;
    o.height = height;
  } else {
    const imgRatio = width / height;
    const ctxRatio = targetWidth / targetHeight;

    if (imgRatio >= ctxRatio) {
      // Greater ratio
      o.width = targetWidth;
      o.height = (targetWidth * height) / width;
    } else {
      // Lower ratio
      o.height = targetHeight;
      o.width = (targetHeight * width) / height;
    }
  }

  return o;
}

/**
 *
 * @param {Image} img
 * @param {number} width
 * @param {number} height
 * @param {boolean} expand
 * @return {HTMLCanvasElement}
 */
export function scaleDownImage(img, { width, height }, expand = false) {
  if (!(img instanceof Image)) {
    throw new TypeError('The provided img must be an Image instance');
  }

  checkImageSizeNotZero(img, true);

  const newDimensions = fitArea(
    img,
    {
      width,
      height,
    },
    expand
  );

  const newWidth = Math.round(newDimensions.width);
  const newHeight = Math.round(newDimensions.height);

  const canvas = document.createElement('canvas');
  canvas.width = newWidth;
  canvas.height = newHeight;

  const ctx = canvas.getContext('2d');

  ctx.drawImage(img, 0, 0, newWidth, newHeight);

  return canvas;
}

/**
 *
 * @param {Image} img
 * @param {number} x
 * @param {number} y
 * @param {number} width
 * @param {number} height
 * @param {number|undefined} outputWidth
 * @param {number|undefined} outputHeight
 * @return {HTMLCanvasElement}
 */
export function cropImage(
  img,
  { x = 0, y = 0, width, height } = {},
  { width: outputWidth, height: outputHeight } = {}
) {
  const normalizedWidth = Math.round(width);
  const normalizedHeight = Math.round(height);
  const normalizedOutputWidth = outputWidth
    ? Math.round(outputWidth)
    : normalizedWidth;
  const normalizedOutputHeight = outputHeight
    ? Math.round(outputHeight)
    : normalizedHeight;

  const canvas = document.createElement('canvas');
  canvas.width = normalizedOutputWidth;
  canvas.height = normalizedOutputHeight;

  const ctx = canvas.getContext('2d');

  ctx.drawImage(
    img,
    x,
    y,
    normalizedWidth,
    normalizedHeight,
    0,
    0,
    normalizedOutputWidth,
    normalizedOutputHeight
  );

  return canvas;
}

/**
 *
 * @param {HTMLCanvasElement} canvas
 * @param {string} mimeType
 * @param {number} qualityArgument
 * @return {Promise<Blob>}
 */
export async function canvasToBlob(canvas, { mimeType, qualityArgument } = {}) {
  return new Promise(resolve =>
    canvas.toBlob(blob => resolve(blob), mimeType, qualityArgument)
  );
}

/**
 *
 * @param {HTMLCanvasElement} canvas
 * @param {string} mimeType
 * @param {number} qualityArgument
 * @param {boolean} keepObjectUrl
 * @return {Promise<Image>}
 */
export async function canvasToImage(
  canvas,
  { mimeType, qualityArgument, keepObjectUrl = false } = {}
) {
  return canvasToBlob(canvas, { mimeType, qualityArgument }).then(blob =>
    blobToImage(blob, keepObjectUrl)
  );
}

/**
 *
 * @param {HTMLCanvasElement} canvas
 * @param {string} mimeType
 * @param {number} qualityArgument
 * @returns {Promise<string>}
 */
export async function canvasToObjectUrl(
  canvas,
  { mimeType, qualityArgument } = {}
) {
  return canvasToBlob(canvas, { mimeType, qualityArgument }).then(blob =>
    URL.createObjectURL(blob)
  );
}

/**
 *
 * @param {File|Blob} blob
 * @param {boolean} keepObjectUrl
 * @return {Promise<Image>}
 */
export async function blobToImage(blob, keepObjectUrl = false) {
  const blobUrl = URL.createObjectURL(blob);

  const imagePromise = loadImage(blobUrl);

  if (!keepObjectUrl) {
    // No longer need to read the blob so it's revoked
    imagePromise.then(() => URL.revokeObjectURL(blobUrl));
  }

  return imagePromise;
}
